import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { getApiUrl } from '../utils/apiConfig';
import DienstplanKalender from '../components/DienstplanKalender';

// Öffentlicher Dienstplan ohne Login: PIN identifiziert den Mitarbeiter.
// Ohne gültige PIN sind nur Tage mit geplanten Schichten sichtbar, keine Details.
export default function DienstplanPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Ohne PIN: nur [{ datum, anzahl }]; Details kommen erst PIN-geprüft über /meine
  const [tage, setTage] = useState([]);
  const [schichten, setSchichten] = useState([]);
  const [pin, setPin] = useState('');
  const [vorname, setVorname] = useState('');
  const [meineSchichten, setMeineSchichten] = useState([]);
  const [pinOk, setPinOk] = useState(false);

  const api = useMemo(() => axios.create(), []);

  const tageMap = useMemo(() => {
    const map = {};
    tage.forEach((t) => { map[t.datum] = t.anzahl; });
    return map;
  }, [tage]);

  const loadInfo = useCallback(async () => {
    if (!token) {
      setError('Kein Dienstplan-Code in der URL gefunden');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(getApiUrl('/api/dienstplan/info'), { params: { token } });
      if (!data?.success) throw new Error(data?.error || 'Dienstplan konnte nicht geladen werden');
      setTage(data.tage || []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Dienstplan konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [api, token]);

  useEffect(() => { loadInfo(); }, [loadInfo]);

  // Nach vollständiger PIN automatisch Details + eigene Anmeldungen laden (4-stellige Bestands-PINs + 6-stellige neue PINs)
  useEffect(() => {
    if (!/^\d{4}$|^\d{6}$/.test(pin)) {
      setPinOk(false);
      setSchichten([]);
      setMeineSchichten([]);
      setVorname('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(getApiUrl('/api/dienstplan/meine'), { token, pin });
        if (cancelled) return;
        if (data?.success) {
          setPinOk(true);
          setVorname(data.vorname || '');
          setSchichten(data.schichten || []);
          setMeineSchichten(data.meine_schichten || []);
          setError('');
        }
      } catch (err) {
        if (cancelled) return;
        setPinOk(false);
        setSchichten([]);
        // Bei 4 Ziffern keinen Fehler zeigen – Nutzer tippt evtl. noch eine 6-stellige PIN
        if (pin.length === 6) {
          setError(err?.response?.data?.error || 'PIN ungültig');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [api, token, pin]);

  const handleAnmelden = async (schicht, von, bis) => {
    setSubmitting(true);
    setError('');
    try {
      const { data } = await api.post(getApiUrl('/api/dienstplan/anmeldung'), { token, pin, schicht_id: schicht.schicht_id, zeit_von: von, zeit_bis: bis });
      if (!data?.success) throw new Error(data?.error || 'Aktion fehlgeschlagen');
      setSchichten(data.schichten || []);
      setMeineSchichten(data.meine_schichten || []);
      return true;
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Aktion fehlgeschlagen');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleAbmelden = async (schicht) => {
    setSubmitting(true);
    setError('');
    try {
      const { data } = await api.post(getApiUrl('/api/dienstplan/abmeldung'), { token, pin, schicht_id: schicht.schicht_id });
      if (!data?.success) throw new Error(data?.error || 'Aktion fehlgeschlagen');
      setSchichten(data.schichten || []);
      setMeineSchichten(data.meine_schichten || []);
      return true;
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Aktion fehlgeschlagen');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', display: 'flex', justifyContent: 'center', p: 2 }}>
      <Card sx={{ width: '100%', maxWidth: 560, alignSelf: 'flex-start' }}>
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="overline" color="text.secondary">Dienstplan</Typography>
              <Typography variant="h5">Arbeitstage</Typography>
            </Box>

            {error ? <Alert severity="error">{error}</Alert> : null}

            <TextField
              label="Deine PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6, style: { textAlign: 'center', fontSize: '1.4rem', letterSpacing: '0.5em' } }}
              fullWidth
            />
            {pinOk ? (
              <Alert icon={<CheckCircle fontSize="inherit" />} severity="success">
                Hallo{vorname ? ` ${vorname}` : ''}! Tippe auf einen freigeschalteten Tag und wähle eine Schicht,
                um dich ein- oder auszutragen.
              </Alert>
            ) : null}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : (
              <DienstplanKalender
                schichten={schichten}
                meineSchichten={meineSchichten}
                tageMap={tageMap}
                canEdit={pinOk}
                lockedHinweis="Details siehst du nach Eingabe deiner PIN."
                submitting={submitting}
                onAnmelden={handleAnmelden}
                onAbmelden={handleAbmelden}
              />
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
